const CACHE_VERSION = "v1";

/**
 * Compute SHA-256 hash of input string, prefixed with cache version.
 * Uses Web Crypto API (available in all modern browsers).
 */
export async function computeCacheHash(input: string): Promise<string> {
  const data = new TextEncoder().encode(`${CACHE_VERSION}:${input}`);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function meaningCacheHash(transcript: string): Promise<string> {
  return computeCacheHash(transcript);
}

/**
 * Voice fingerprint hash. Uses channelId only (not latestVideoId) because
 * this function doesn't have access to the latest video ID. The 48h TTL
 * is the primary invalidation mechanism.
 */
export async function voiceFingerprintCacheHash(
  channelId: string
): Promise<string> {
  return computeCacheHash(`voice:${channelId}`);
}

/**
 * Frame summary hash. Uses video file hash + transcript hash (NOT frame blob hashes)
 * so the cache check happens BEFORE the expensive frame extraction step.
 */
export async function frameSummaryCacheHash(
  videoFileHash: string,
  transcriptHash: string
): Promise<string> {
  return computeCacheHash(`frames:${videoFileHash}:${transcriptHash}`);
}

export async function intelligenceCacheHash(
  channelId: string,
  tabType: string,
  language: string
): Promise<string> {
  return computeCacheHash(`intel:${channelId}:${tabType}:${language}`);
}

export async function nicheCacheHash(
  channelId: string,
  videoCount: number
): Promise<string> {
  return computeCacheHash(`niche:${channelId}:${videoCount}`);
}

export async function thumbnailStyleCacheHash(
  channelId: string,
  thumbnailCount: number
): Promise<string> {
  return computeCacheHash(`style:${channelId}:${thumbnailCount}`);
}

/** Current cache version. Used for orphan cleanup on version bump. */
export const CURRENT_CACHE_VERSION = CACHE_VERSION;
